import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import { changePinCode } from "../../../controller/settingsController/changePinController";
import AppConstants from "../../../utils/appConstants";
import { useToast } from "../../../contexts/ToastContext";
import {
  getPasswordRequirements,
  PASSWORD_REQUIREMENT_KEYS,
} from "../../../utils/passwordRequirements";
import "../../css/changePin.css";

export default function ChangePin() {
  const { t } = useTranslation("mobile");
  const { t: tReg } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const [user, setUser] = useState(null);
  const [oldPin, setOldPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [showOldPin, setShowOldPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } catch (_) {}
  }, []);

  const passwordRequirements = getPasswordRequirements(newPin);
  const allPasswordRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  const handleFieldChange = (field, setter) => (e) => {
    setter(e.target.value);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!oldPin.trim()) {
      newErrors.oldPin = t("enter_your_password", { defaultValue: "Enter your password" });
    }

    if (!newPin.trim()) {
      newErrors.newPin = t("enter_your_password", { defaultValue: "Enter your password" });
    } else if (!allPasswordRequirementsMet) {
      newErrors.newPin = tReg("personalInformation.password.errors.passwordPolicy");
    }

    if (!confirmPin.trim()) {
      newErrors.confirmPin = t("enter_your_password", { defaultValue: "Enter your password" });
    } else if (confirmPin !== newPin) {
      newErrors.confirmPin = t("passwords_do_not_match", { defaultValue: "Passwords do not match" });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setProcessing(true);
      setErrors({});

      const data = await changePinCode(oldPin, newPin, confirmPin);
      const code = Number(data?.CODE);

      if (code === AppConstants.responseOk) {
        showSuccess(
          t("success", { defaultValue: "Success" }),
          t("password_changed_successfully", { defaultValue: "Password changed successfully" })
        );
        setOldPin("");
        setNewPin("");
        setConfirmPin("");
      } else if (code === AppConstants.responseError) {
        showError(
          t("error", { defaultValue: "Error" }),
          t("old_password_incorrect", { defaultValue: "Old password is incorrect" })
        );
      } else {
        showError(
          t("error", { defaultValue: "Error" }),
          data?.MESSAGE ||
            t("failed_to_change_password_try_again", {
              defaultValue: "Failed to change password. Please try again.",
            })
        );
      }
    } catch (error) {
      showError(
        t("error", { defaultValue: "Error" }),
        error.message ||
          t("failed_to_change_password_try_again", {
            defaultValue: "Failed to change password. Please try again.",
          })
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="change-pin-page">
      <Sidebar user={user} />
      <div className="change-pin-content">
        <h1 className="change-pin-title">
          {t("change_password", { defaultValue: "Change Password" })}
        </h1>

        <div className="change-pin-container">
          <div className="change-pin-form">
            <div className="change-pin-form-group">
              <label className="change-pin-form-label">
                {t("old_password", { defaultValue: "Old Password" })}
              </label>
              <div className="change-pin-input-wrapper">
                <span className="change-pin-input-icon">
                  <i className="fa-solid fa-lock"></i>
                </span>
                <CustomInput
                  type={showOldPin ? "text" : "password"}
                  placeholder={t("enter_your_password", { defaultValue: "Enter your password" })}
                  value={oldPin}
                  onChange={handleFieldChange("oldPin", setOldPin)}
                  className={`change-pin-input ${errors.oldPin ? "change-pin-input-error" : ""}`}
                  disabled={processing}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="change-pin-visibility-btn"
                  onClick={() => setShowOldPin((prev) => !prev)}
                  disabled={processing}
                  aria-label={
                    showOldPin
                      ? t("hide_old_password", { defaultValue: "Hide old password" })
                      : t("show_old_password", { defaultValue: "Show old password" })
                  }
                >
                  <i className={`fa-solid ${showOldPin ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>
              {errors.oldPin && <div className="change-pin-field-error">{errors.oldPin}</div>}
            </div>

            <div className="change-pin-form-group">
              <label className="change-pin-form-label">
                {t("new_password", { defaultValue: "New Password" })}
              </label>
              <div className="change-pin-input-wrapper">
                <span className="change-pin-input-icon">
                  <i className="fa-solid fa-key"></i>
                </span>
                <CustomInput
                  type={showNewPin ? "text" : "password"}
                  placeholder={tReg("personalInformation.password.placeholders.password")}
                  value={newPin}
                  onChange={handleFieldChange("newPin", setNewPin)}
                  className={`change-pin-input ${errors.newPin ? "change-pin-input-error" : ""}`}
                  disabled={processing}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="change-pin-visibility-btn"
                  onClick={() => setShowNewPin((prev) => !prev)}
                  disabled={processing}
                  aria-label={
                    showNewPin
                      ? t("hide_new_password", { defaultValue: "Hide new password" })
                      : t("show_new_password", { defaultValue: "Show new password" })
                  }
                >
                  <i className={`fa-solid ${showNewPin ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>
              {errors.newPin && <div className="change-pin-field-error">{errors.newPin}</div>}
            </div>

            <div className="change-pin-form-group">
              <label className="change-pin-form-label">
                {t("confirm_new_password", { defaultValue: "Confirm New Password" })}
              </label>
              <div className="change-pin-input-wrapper">
                <span className="change-pin-input-icon">
                  <i className="fa-solid fa-key"></i>
                </span>
                <CustomInput
                  type={showConfirmPin ? "text" : "password"}
                  placeholder={tReg("personalInformation.password.placeholders.passwordConfirmation")}
                  value={confirmPin}
                  onChange={handleFieldChange("confirmPin", setConfirmPin)}
                  className={`change-pin-input ${errors.confirmPin ? "change-pin-input-error" : ""}`}
                  disabled={processing}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="change-pin-visibility-btn"
                  onClick={() => setShowConfirmPin((prev) => !prev)}
                  disabled={processing}
                  aria-label={
                    showConfirmPin
                      ? t("hide_confirm_password", { defaultValue: "Hide confirm password" })
                      : t("show_confirm_password", { defaultValue: "Show confirm password" })
                  }
                >
                  <i className={`fa-solid ${showConfirmPin ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>
              {errors.confirmPin && <div className="change-pin-field-error">{errors.confirmPin}</div>}
            </div>

            <ul className="password-requirements change-pin-password-requirements" aria-live="polite">
              {PASSWORD_REQUIREMENT_KEYS.map((key) => {
                const met = passwordRequirements[key];
                return (
                  <li key={key} className={`password-requirement ${met ? "met" : ""}`}>
                    <span className="password-requirement-box" aria-hidden="true">
                      {met ? <i className="fa-solid fa-check" /> : null}
                    </span>
                    <span>{tReg(`personalInformation.password.requirements.${key}`)}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="change-pin-actions">
            <CustomButton
              className="change-pin-submit-button"
              variant="primary"
              onClick={handleSubmit}
              disabled={processing || !allPasswordRequirementsMet}
            >
              <span>
                {processing
                  ? t("changing", { defaultValue: "Changing..." })
                  : t("change_password", { defaultValue: "Change Password" })}
              </span>
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
}
